import { doFetch, type RequestConfig } from "@coideo/barrani";
import { queryClient } from "context/query-context";

import StorageItem from "./StorageItem";

const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "";

let refreshPromise: Promise<void> | null = null;

const tokenItem = new StorageItem("__web_pos_token__");
const refreshTokenItem = new StorageItem("__web_pos_refresh_token__");
const shopItem = new StorageItem("__web_pos_shop_url__");

export type Tokens = { access_token: string; refresh_token: string };

const saveTokens = (tokens: Tokens) => {
  tokenItem.save(tokens.access_token);
  refreshTokenItem.save(tokens.refresh_token);
};

const removeTokens = () => {
  tokenItem.remove();
  refreshTokenItem.remove();
};

function logout(): never {
  queryClient.clear();
  removeTokens();
  window.location.assign(window.location.pathname);

  throw new Error("Por favor, vuelve a iniciar sesión.");
}

const getAuthToken = () => {
  const token = tokenItem.get();

  return token ? `Bearer ${token}` : "";
};

async function client<T>(
  endpoint: string,
  { handle401 = true, headers, ...config }: RequestConfig & { handle401?: boolean } = {},
) {
  if (refreshPromise) await refreshPromise;

  const url = `${API_URL}/${endpoint}`;
  const getConfig = () => ({ headers: { ...headers, Authorization: getAuthToken() }, ...config });

  return await doFetch<T>(url, getConfig()).catch(async (e) => {
    if (handle401 && e.status === 401) {
      if (!refreshTokenItem.get()) return logout();
      if (!refreshPromise) refreshPromise = doRefreshToken();
      await refreshPromise;

      return await doFetch<T>(url, getConfig());
    }
    throw e;
  });
}

async function doRefreshToken() {
  await doFetch<Tokens>(`${API_URL}/oauth/access_token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      "Content-Length": "0",
    },
    queryString: {
      grant_type: "refresh_token",
      refresh_token: refreshTokenItem.get(),
      url: shopItem.get(),
    },
  })
    .then(saveTokens)
    .catch(logout)
    .finally(() => {
      refreshPromise = null;
    });
}

const getToken = tokenItem.get;
const saveShopUrl = shopItem.save;

export { client, logout, saveTokens, getToken, saveShopUrl };
